import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { useAutomationsOnHoldByTypeQuery, useAutomationsOnHoldQuery } from '../../../../queries/Automation';
import automationsNotPendingImg from '../../../assets/automations-not-pending.svg';
import { Button } from '../../../components-v2/Button';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../components-v2/Select';
import { MultiContainerHeader } from '../../MultiContainerHeader';
import { AutomationPendingIcon } from '../../svgs/graphics/AutomationPendingIcon';
import { AutomationOnHold, AutomationOnHoldRef } from '../AutomationOnHold';
import { AutomationStartedNotification } from '../AutomationStartedNotification';
import { styleSheet } from './styles';

const PendingAutomationOptions: { id: string; text: string; value: string }[] = [
	{
		id: 'pending-automation-option-mine',
		text: `My Pending Automations`,
		value: 'me',
	},
	{
		id: 'pending-automation-option-all',
		text: `All Pending Automations`,
		value: 'all',
	},
];

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

export const AutomationsOnHold: React.FC<IProps> = ({ className, styles }) => {
	const { resourceSelectorId } = useParams<{ resourceSelectorId?: string }>();
	const userSession = useUserSession();
	const _errorMessages = useErrorMessages();
	const { logApiError: _logApiError } = useEventLogging('AutomationsOnHold');

	const [selectedShow, setSelectedShow] = React.useState('me');
	const [showAutomationStartedNotification, setShowAutomationStartedNotification] = React.useState(false);
	const [automationCompletionState, setAutomationCompletionState] = React.useState<{
		myComplete: boolean;
		allComplete: boolean;
	}>({ myComplete: false, allComplete: false });

	// Track which automations have selections for the "Approve all selected" button.
	const [automationSelections, setAutomationSelections] = React.useState<Map<string, boolean>>(new Map());
	// Store refs to AutomationOnHold components for calling approveSelected.
	const automationRefs = React.useRef<Map<string, AutomationOnHoldRef>>(new Map());

	const canSendOnBehalf = React.useMemo(() => {
		return (
			userSession?.account?.features?.automation?.allowAdminToStartOnBehalf &&
			resourceSelectorId === Api.ResourceSelectorId.AutomationsOnHold
		);
	}, [userSession?.account?.features?.automation?.allowAdminToStartOnBehalf, resourceSelectorId]);

	const isShowAllSelected = React.useCallback(() => {
		return selectedShow === 'all';
	}, [selectedShow]);

	// New* selectors always use includeAll: true. Standard selector uses the dropdown selection.
	const isNewEntitySelector = [
		Api.ResourceSelectorId.NewDonorAutomationsOnHold,
		Api.ResourceSelectorId.NewLeadAutomationsOnHold,
		Api.ResourceSelectorId.NewClientAutomationsOnHold,
	].includes(resourceSelectorId as Api.ResourceSelectorId);

	const includeAll = React.useMemo(() => {
		if (isNewEntitySelector) {
			return true;
		}
		return isShowAllSelected() && canSendOnBehalf;
	}, [isNewEntitySelector, isShowAllSelected, canSendOnBehalf]);

	// Use the standard query for AutomationsOnHold selector.
	const { data: standardAutomationsData, refetch: refetchStandardAutomations } = useAutomationsOnHoldQuery({
		includeAll,
		enabled: resourceSelectorId === Api.ResourceSelectorId.AutomationsOnHold,
	});

	// Use the resource selector query for New* selectors (filters by automation trigger type).
	const { data: newEntityAutomationsData, refetch: refetchNewEntityAutomations } = useAutomationsOnHoldByTypeQuery({
		typeOf: resourceSelectorId as Api.ResourceSelectorId,
		enabled: isNewEntitySelector,
	});

	// Select the appropriate data based on the resource selector type.
	const automationsOnHoldData = isNewEntitySelector ? newEntityAutomationsData : standardAutomationsData;
	const refetchAutomations = isNewEntitySelector ? refetchNewEntityAutomations : refetchStandardAutomations;

	const title = React.useMemo(() => {
		switch (resourceSelectorId) {
			case Api.ResourceSelectorId.NewLeadAutomationsOnHold: {
				return 'New Lead Pending Automations';
			}
			case Api.ResourceSelectorId.NewDonorAutomationsOnHold: {
				return 'New Donor Pending Automations';
			}
			case Api.ResourceSelectorId.NewClientAutomationsOnHold: {
				return 'New Client Pending Automations';
			}
			default: {
				break;
			}
		}
		return 'Pending Automations';
	}, [resourceSelectorId]);

	const checkShowingStartedNotification = React.useCallback(() => {
		if (isShowAllSelected() && automationCompletionState.allComplete) {
			setShowAutomationStartedNotification(true);
		} else if (selectedShow === 'me' && automationCompletionState.myComplete) {
			setShowAutomationStartedNotification(true);
		}
	}, [isShowAllSelected, automationCompletionState, selectedShow]);

	const onApproveCompleted = React.useCallback(
		(templateId: string, wasIncludeAll: boolean) => {
			if (wasIncludeAll) {
				setAutomationCompletionState(prev => ({ ...prev, allComplete: true }));
			} else {
				setAutomationCompletionState(prev => ({ ...prev, myComplete: true }));
			}
			refetchAutomations();
		},
		[refetchAutomations]
	);

	const onShowChanged = React.useCallback((value: string) => {
		setSelectedShow(value);
		setAutomationCompletionState({ myComplete: false, allComplete: false });
	}, []);

	// Handler for when an automation's selection state changes.
	const onSelectionChange = React.useCallback((templateId: string, hasSelection: boolean) => {
		setAutomationSelections(prev => {
			const next = new Map(prev);
			next.set(templateId, hasSelection);
			return next;
		});
	}, []);

	// Disable "Approve all selected" when no automation has any selections.
	const disableApproveAll = React.useMemo(() => {
		const automations = automationsOnHoldData || [];
		if (automations.length === 0) {
			return true;
		}
		// Return true (disabled) if all automations have no selections.
		return Array.from(automationSelections.values()).every(hasSelection => !hasSelection);
	}, [automationsOnHoldData, automationSelections]);

	// Approve all selected contacts across all displayed automations.
	// Busy children are skipped via the isBusy() guard to prevent the race condition.
	const approveAllSelected = React.useCallback(() => {
		automationRefs.current.forEach(automationRef => {
			if (automationRef.hasSelectedContacts() && !automationRef.isBusy()) {
				automationRef.approveSelected();
			}
		});
	}, []);

	const renderAutomationsOnHold = React.useCallback(() => {
		const automations = automationsOnHoldData || [];

		if (automations.length === 0) {
			return (
				<div className={css(styleSheet.nonePending)}>
					<div className={css(styleSheet.nonePendingGraphic)}>
						<img src={automationsNotPendingImg} alt='' />
					</div>
					<div className={css(styleSheet.nonePendingText)}>You don&apos;t have any pending automations.</div>
				</div>
			);
		}

		return (
			<>
				{automations.map(x => {
					const templateId = x.template?.id;
					return (
						<AutomationOnHold
							key={templateId}
							ref={el => {
								if (el && templateId) {
									automationRefs.current.set(templateId, el);
								} else if (templateId) {
									automationRefs.current.delete(templateId);
								}
							}}
							automationOnHold={x}
							includeAll={includeAll}
							onApproveCompleted={onApproveCompleted}
							onSelectionChange={onSelectionChange}
						/>
					);
				})}
			</>
		);
	}, [automationsOnHoldData, includeAll, onApproveCompleted, onSelectionChange]);

	React.useEffect(() => {
		if (selectedShow) {
			refetchAutomations();
		}
	}, [selectedShow, refetchAutomations]);

	React.useEffect(() => {
		checkShowingStartedNotification();
	}, [checkShowingStartedNotification]);

	// Auto-switch from "My Pending Automations" to "All Pending Automations" when my automations are empty.
	// This matches the old implementation behavior for admins who can send on behalf.
	React.useEffect(() => {
		const automations = automationsOnHoldData || [];
		if (
			selectedShow === 'me' &&
			canSendOnBehalf &&
			automations.length === 0 &&
			resourceSelectorId === Api.ResourceSelectorId.AutomationsOnHold
		) {
			setSelectedShow('all');
		}
	}, [automationsOnHoldData, selectedShow, canSendOnBehalf, resourceSelectorId]);

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} bulk-email-edit-body ${className || ''}`}>
			<MultiContainerHeader fullscreenHeader={title} />
			{showAutomationStartedNotification ? (
				<AutomationStartedNotification className={css(styleSheet.complete)} />
			) : (
				<>
					<div className={css(styleSheet.headerContainer)}>
						<div className={css(styleSheet.flexBetween)}>
							<div>
								<div className={css(styleSheet.titleContainer)}>
									<AutomationPendingIcon />
									<div className={css(styleSheet.title)}>Pending Automations</div>
								</div>
							</div>
							{resourceSelectorId === Api.ResourceSelectorId.AutomationsOnHold && (
								<div className={css(styleSheet.flex)}>
									{canSendOnBehalf && (
										<Select value={selectedShow} onValueChange={onShowChanged}>
											<SelectTrigger styles={styleSheet.select}>
												<span>{PendingAutomationOptions.find(o => o.value === selectedShow)?.text}</span>
											</SelectTrigger>
											<SelectContent>
												{PendingAutomationOptions.map(option => (
													<SelectItem key={option.id} value={option.value}>
														{option.text}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
									<Button variant='primary' disabled={disableApproveAll} onClick={approveAllSelected}>
										Approve all selected
									</Button>
								</div>
							)}
						</div>
						{canSendOnBehalf ? (
							<div className={css(styleSheet.message)}>
								As an admin, you can approve and/or reject automations for other contact owners on the account. Once
								approved, the automation starts, and the owner will be notified.
							</div>
						) : (
							<div className={css(styleSheet.message)}>
								This account only allows automation approval and rejection from the contact owner.
							</div>
						)}
					</div>
					{renderAutomationsOnHold()}
				</>
			)}
		</div>
	);
};
